// packages/web/src/lib/readFailure.ts — S15: A REFUSED READ IS NOT AN EMPTY
// SHOP.
//
// 2026-08-13, in the real product: Huan opened Rexx, walked past the first
// screen, and every screen after it said there was nothing there. Then a raw
// `{"error":{"code":"rate_limited"}}` came back. The work-order list's ~250
// link reads had eaten 83% of the 300-requests-per-minute limiter
// (`security.global_rate_limit_per_minute`) in one screen visit, and the
// screens rendered the refusals as ABSENCE — they told him his shop was
// empty while the server was saying no.
//
// This chunk removes the cause (one read for many links). This module
// removes the SECOND defect, which would have survived it: "there are none"
// and "we could not read them" are different facts and must read as
// different sentences. It is the same rule the work-order list already
// honours for a client name it could not resolve — a cell never goes blank,
// it says which truth it is — applied to the read that fails wholesale.
//
// A 429 in particular deserves its own words: it is temporary, nothing is
// wrong with the data, and trying again works. Telling someone that is a
// completely different act from showing them an empty list.
import { ApiError } from './api';

export type ReadFailureKind = 'rate_limited' | 'unreachable' | 'refused' | 'server';

export interface ReadFailure {
  kind: ReadFailureKind;
  /** The sentence that replaces the empty state. */
  headline: string;
  /** The quieter second line: what is actually true right now. */
  detail: string;
  /** Whether trying the same read again is a sensible thing to offer. */
  retryable: boolean;
}

/** THE ONE PLAIN SENTENCE, for when the server has nothing sayable of its own.
 *
 * Not a vaguer copy of a better sentence — there was no better sentence. It
 * says what is true (nothing is broken), and it offers a reach rather than a
 * dead end, which is the product's own posture toward a refusal. */
const REFUSAL_IN_OUR_OWN_WORDS =
  'Nothing here is missing or broken — this screen belongs to another seat. Ask whoever set up this workspace if you need it.';

/**
 * ⚑ IS THIS REFUSAL THE ENGINE TALKING TO ITSELF? (2026-09-15, the Day 1 walk.)
 *
 * Glee — a child, nine — opened `#/people` and read:
 *
 *   This isn't yours to read.
 *   role 'member' does not meet record type 'aa_crew_credit''s minimum role 'staff'
 *
 * `#/money` leaked the same shape with `aa_subject` in it. That second line is
 * `AuthorizationError`'s message, written for whoever is reading a stack
 * trace, and it names two internal vocabularies at once.
 *
 * ⚠ THE CANON'S RULE STANDS — "show the server's own sentence, never a vaguer
 * second copy" — and blanket-replacing every 403 BROKE it: the conversation
 * gates answer with real sentences meant for the reader ("it is not on your
 * jobs and no grant names you"; "that conversation lane is not available to
 * your role"), and swallowing those is the exact defect the rule names. So
 * this recognises the ENGINE's sentences and only those.
 *
 * The tells are its nouns, not a guess at tone. Every generic refusal Core
 * throws carries one of them (packages/core/src/enforcement.ts's role/status/
 * membership guards, records.ts's role and capability gates); no hand-written
 * refusal in the product carries any. `readFailure.test`'s sibling —
 * src/test/ReadFailureRefusal.test.ts — pins both halves with the real
 * sentences from both sides.
 *
 * ⚠ AND THIS IS A BACKSTOP, NOT THE CURE. The durable fix is that the engine's
 * message never leaves the process at all — a 403 composed for HTTP should
 * carry a sentence somebody wrote. That is an every-route change and it is
 * named in the tranche report rather than smuggled in here.
 */
const ENGINE_TELLS: readonly RegExp[] = [
  /\bminimum role\b/, //         "…does not meet the operation's minimum role 'admin'"
  /\bmembership\b/, //           "no membership for user <uuid> in tenant <uuid>", "…this membership does not have"
  /\bdefault-deny\b/, //         "…access is scoped (default-deny)"
  /\bnon-system-actor\b/, //     "missing userId for a non-system-actor call"
];

function readsAsMachinery(message: string): boolean {
  return ENGINE_TELLS.some((tell) => tell.test(message));
}

/** What went wrong, in the product's own voice. Never returns null: a read
 * that threw always has something honest to say. */
export function describeReadFailure(err: unknown): ReadFailure {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      return {
        kind: 'rate_limited',
        headline: 'This screen asked for too much at once.',
        detail:
          'The server is holding us off for a moment. Nothing here is lost or empty — the same read will work again shortly.',
        retryable: true,
      };
    }
    if (err.status === 401 || err.status === 403) {
      return {
        kind: 'refused',
        headline: "This isn't yours to read.",
        // ⚑ THE SERVER'S SENTENCE — UNLESS IT IS THE ENGINE TALKING TO ITSELF
        // (2026-09-15, the Day 1 walk). See `readsAsMachinery` below.
        detail: readsAsMachinery(err.message) ? REFUSAL_IN_OUR_OWN_WORDS : err.message,
        retryable: false,
      };
    }
    return {
      kind: 'server',
      headline: "The server couldn't answer.",
      detail: err.message,
      retryable: true,
    };
  }
  return {
    kind: 'unreachable',
    headline: "We couldn't reach the server.",
    detail: 'Your connection dropped or the server is down. What you can see is what we read before it went.',
    retryable: true,
  };
}
